import { ReactNode, useId } from 'react';

import { FOCO_VISIVEL } from 'utils/foco';

import { FaseDoCiclo, Periodo } from '../realizacoes-do-semestre';

const PERIODOS: { valor: Periodo; rotulo: string }[] = [
  { valor: 'ciclo', rotulo: 'Ciclo inteiro' },
  { valor: 'mes', rotulo: 'Mês' },
];

const ROTULO = 'mb-[7px] block text-[11px] font-bold uppercase tracking-[0.05em] text-content-soft';

interface Props {
  periodo: Periodo;
  onPeriodo: (periodo: Periodo) => void;
  /** `dicaDoPeriodo` do período escolhido (RDV-08, RDV-09). */
  dica: string;
  /** `mesesDoCiclo`: um chip por fase mensal iniciada, com os cartões cadastrados nela; antes da hora do servidor, nada. */
  meses?: { fase: FaseDoCiclo; quantidade: number }[];
  mesEscolhido?: string;
  onMes: (faseId: string) => void;
  /** Seletor de ciclo, que divide o painel com o período, como no design. */
  children?: ReactNode;
}

/** RDV-07 e RDV-09: "Ciclo inteiro" / "Mês" e, em "Mês", a linha "Mês de cadastramento" com os chips. */
export default function FiltroDePeriodo({ periodo, onPeriodo, dica, meses, mesEscolhido, onMes, children }: Props) {
  const periodoId = useId();
  const mesId = useId();

  return (
    <div className="mt-3.5 rounded-jj-panel bg-brand-white px-[22px] pb-4 pt-[18px] shadow-jj-01">
      <div className="flex flex-wrap items-end gap-[18px]">
        {children}
        <div>
          <span id={periodoId} className={ROTULO}>
            Período
          </span>
          <div role="group" aria-labelledby={periodoId} className="inline-flex gap-1 rounded-jj-md bg-surface-100 p-1">
            {PERIODOS.map(opcao => (
              <button
                key={opcao.valor}
                type="button"
                aria-pressed={periodo === opcao.valor}
                onClick={() => onPeriodo(opcao.valor)}
                className={`cursor-pointer whitespace-nowrap rounded-jj-md border-0 px-4 py-[9px] font-display text-xs font-bold ${FOCO_VISIVEL} ${
                  periodo === opcao.valor ? 'bg-brand-blue text-brand-yellow' : 'bg-transparent text-content-soft'
                }`}
              >
                {opcao.rotulo}
              </button>
            ))}
          </div>
        </div>
        <p className="m-0 max-w-[300px] self-center text-[11.5px] leading-[1.5] text-content-soft lg:ml-auto">{dica}</p>
      </div>

      {periodo === 'mes' && (
        <div
          role="group"
          aria-labelledby={mesId}
          className="mt-3.5 flex flex-wrap items-center gap-2 border-0 border-t border-solid border-surface-200 pt-3.5"
        >
          <span id={mesId} className="mr-1 text-[11px] font-bold uppercase tracking-[0.05em] text-content-soft">
            Mês de cadastramento
          </span>
          {meses?.length === 0 && (
            <span className="text-[11.5px] text-content-soft">Nenhum mês de cadastramento iniciado neste ciclo.</span>
          )}
          {meses?.map(({ fase, quantidade }) => {
            const escolhido = fase._id === mesEscolhido;
            return (
              <button
                key={fase._id}
                type="button"
                aria-pressed={escolhido}
                onClick={() => onMes(fase._id)}
                className={`inline-flex cursor-pointer items-center gap-[7px] rounded-jj-pill border-2 border-solid px-3.5 py-2 font-display text-xs font-bold text-brand-navy ${FOCO_VISIVEL} ${
                  escolhido ? 'border-brand-pink bg-brand-white' : 'border-transparent bg-surface-100'
                }`}
              >
                {fase.title}
                <span className="rounded-jj-pill bg-brand-blue px-[7px] py-px text-[10px] text-brand-yellow">{quantidade}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
